import React, { useState, useMemo, useEffect, Fragment } from 'react';
import kebabCase from 'lodash/kebabCase';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import merge from 'lodash/merge';
import classNames from 'classnames';
import type { CmsPageCollectionItems } from '$cms/types';
import type { ContentComponent } from '$cms/ContentComponent';
import { useCmsPageContext } from '$cms/CmsPageContext';
import { replaceCollectionPlaceholders } from '$cms/replaceCollectionPlaceholders';
import { useAppContext } from '$util/AppContext';
import type { AppContextGlobal } from '$util/AppContextGlobal';
import { useResizeViewport } from '$util/hooks/useResizeViewport';
import type { MakeContentfulRichTextRendererOptions } from '@/cms/richTextContentHelpers';
import { renderRichTextHeading } from '@/server/cms/util/renderRichTextHeading';
import { HeroSection } from '@/components/molecules/HeroSection';
import { VrmLookupDumbModal } from '@/components/molecules/VrmLookupDumbModal';
import { VrmLookupDumb } from '@/components/molecules/VrmLookupDumb';
import { translations as vrmLookupModalTranslations } from '@/components/molecules/VrmLookupDumbModal/translations';
import { translations as vrmLookupFormTranslations } from '@/components/molecules/VrmLookupDumb/translations';
import {
  type CmsHeroSectionConfiguration,
  type CmsHeroSectionTranslatedEntities,
  defaultConfiguration,
  CmsHeroSectionSchema,
} from './schema';

import styles from './index.module.scss';

const makePlaceholders = (
  collectionItems: CmsPageCollectionItems
): MakeContentfulRichTextRendererOptions['placeholder'] =>
  mapValues(
    mapKeys(collectionItems, (value, key) => {
      return kebabCase(key);
    }),
    (value) => {
      return value?.name || '';
    }
  );

export const CmsHeroSection: ContentComponent<CmsHeroSectionTranslatedEntities, CmsHeroSectionConfiguration> =
  function CmsHeroSection({ translatedEntities, configuration, className }): JSX.Element {
    const { campaign, locale } = useAppContext<AppContextGlobal>();
    const cmsPageContext = useCmsPageContext();
    const { sku } = merge(defaultConfiguration, configuration);

    const [showStickyButton, setShowStickyButton] = useState(false);

    const [stickyButtonIntersectionObserver, setStickyButtonIntersectionObserver] = useState<
      IntersectionObserver | undefined
    >(undefined);

    const collectionItems = cmsPageContext?.pageRoutes?.[0]?.collectionItems;

    const { heading, subheading, mainCta, grandLabel, partOfRenaultText, partOfRenaultLogo } =
      translatedEntities || {};

    const title = useMemo(() => {
      if ('content' in heading) {
        if (collectionItems) {
          const placeholders = makePlaceholders(collectionItems);

          const richTextContent = renderRichTextHeading(heading.content, {
            ...placeholders,
          });
          return richTextContent;
        }
        return renderRichTextHeading(heading.content);
      }
      return collectionItems ? replaceCollectionPlaceholders(heading.value, collectionItems) : heading.value;
    }, [collectionItems, heading]);

    const subTitle = useMemo(() => {
      if (!subheading) return undefined;
      const subHeading = collectionItems
        ? replaceCollectionPlaceholders(subheading.value, collectionItems)
        : subheading.value;
      /**
       * @note to avoid changing this to a RichText we'll transform '\n' to new lines
       */
      return (
        <>
          {subHeading.split('\\n').map((v, i, arr) => {
            return (
              // eslint-disable-next-line react/no-array-index-key -- not changing index
              <Fragment key={i}>
                {v}
                {i < arr.length - 1 && <br />}
              </Fragment>
            );
          })}
        </>
      );
    }, [collectionItems, subheading]);

    /**
     * @NOTE implementation currently fires the intersection observer on intersection
     * for all viewports because
     *
     * 1. we have not yet moved the useResizeViewport hook
     * to the new grid breakpoints
     * 2. This needs to be run in the useEffect hook so not sure of the best way to
     * do it in both hooks at the moment
     *
     * This means the IntersectionObserver callback will be run for every breakpoint when
     * the intersection ratio threshold is crossed - this is still much better performance
     * than using a scroll listener and with greatly reduce jank already.
     *
     * @TODO extract the Intersection Observers callback function outside the component scope
     *
     * @TODO when the useResizeViewport component is using the new breakpoints,
     * then refactor so that the observer is initialised independtly, that the target is observed
     * when the viewport is matched and unobserved when it does not.
     */
    useEffect(() => {
      const headingElement = document.querySelector<HTMLElement>('.CarHeading');
      if (campaign || !headingElement) return undefined;
      const contentSectionEl = document.querySelector<HTMLElement>('.ContentSectionContainer');
      const topNavBarEl = document.querySelector<HTMLElement>('.TopNavigationBar');
      if (!contentSectionEl || !topNavBarEl) return undefined;

      const isTop =
        (className || '').split(' ').includes('top') &&
        contentSectionEl.previousElementSibling === topNavBarEl.parentElement;

      if (!isTop) {
        contentSectionEl.firstElementChild?.classList.remove('top');
        return undefined;
      }

      topNavBarEl.classList.add('transparent');
      topNavBarEl.classList.add('transition');

      const observerTarget = headingElement;
      const toggleNavBg = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry: IntersectionObserverEntry) => {
          if (window.innerWidth >= 600) {
            if (entry.intersectionRatio < 1) {
              // make nav opaque
              topNavBarEl.classList.remove('transparent');
            } else {
              // make nav transparent
              topNavBarEl.classList.add('transparent');
            }
          }
        });
      };
      const observer = new IntersectionObserver(toggleNavBg, {
        rootMargin: '-80px 0px 0px',
        threshold: [1],
      });

      observer.observe(observerTarget);

      return () => {
        observer.unobserve(observerTarget);
      };
    }, [campaign, className]);

    useEffect(() => {
      const heroVrmElement = document.querySelector<HTMLElement>('.CarVrmForm');
      if (heroVrmElement) {
        const target = heroVrmElement;
        const observer = new IntersectionObserver(
          ([observedTarget]) => {
            const { isIntersecting } = observedTarget;
            if (window.scrollY === 0) {
              setShowStickyButton(false);
            } else {
              setShowStickyButton(!isIntersecting);
            }
          },
          {
            threshold: [0],
          }
        );

        observer.observe(target);

        setStickyButtonIntersectionObserver(observer);

        return () => {
          observer.unobserve(target);
        };
      }
      return () => undefined; // needed due to consistent return lint rule
    }, [setStickyButtonIntersectionObserver, setShowStickyButton]);

    useResizeViewport({
      breakpoint: 'small',
      direction: 'up',
      fn: (isDesktop) => {
        const heroVrmElement = document.querySelector<HTMLElement>('.CarVrmForm');
        // @NOTE if desktop, don't show
        if (heroVrmElement) {
          if (isDesktop) {
            stickyButtonIntersectionObserver?.unobserve(heroVrmElement);
            setShowStickyButton(false);
          } else {
            stickyButtonIntersectionObserver?.observe(heroVrmElement);
          }
        }
      },
    });

    const heroSectionProps = {
      locale,
      heading: title,
      subheading: subTitle,
      mainCta: mainCta?.value,
      grandLabel: grandLabel && renderRichTextHeading(grandLabel.content),
      partOfRenaultText: partOfRenaultText?.value,
      partOfRenaultLogo: partOfRenaultLogo
        ? {
            key: 'partOfRenault',
            img: {
              original_secure_url: partOfRenaultLogo.url,
              width: partOfRenaultLogo.width,
              height: partOfRenaultLogo.height,
            },
            alt: partOfRenaultLogo.alt,
            title: partOfRenaultLogo.title || '',
          }
        : undefined,
      sku,
    };

    return (
      <>
        <HeroSection
          campaignId={campaign?.campaignId}
          className={classNames(CmsHeroSection.displayName, styles.topAdjustment, className)}
          {...heroSectionProps}
        />
        {showStickyButton && (
          <div className={styles.stickyCTA}>
            <VrmLookupDumbModal
              size="L"
              {...vrmLookupModalTranslations[locale].default}
              toggleButtonText={mainCta?.value || vrmLookupModalTranslations[locale].default.toggleButtonText}
            >
              <VrmLookupDumb
                locale={locale}
                sku={sku}
                {...vrmLookupFormTranslations[locale].default}
                campaignId={campaign?.campaignId}
              />
            </VrmLookupDumbModal>
          </div>
        )}
      </>
    );
  };

CmsHeroSection.displayName = 'HeroSection';
CmsHeroSection.schema = CmsHeroSectionSchema;
